/**
 * StatusBadge Component - Reusable status badge using shadcn Badge
 * 
 * Provides consistent status indicators across the application.
 * 
 * @example
 * <StatusBadge status="approved" />
 * <StatusBadge status="pending" variant="warning" />
 */
import { memo } from 'react';
import { Badge, badgeVariants } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { type VariantProps } from 'class-variance-authority';

type BadgeVariant = NonNullable<VariantProps<typeof badgeVariants>['variant']>;

type StatusConfigEntry = {
  label: string;
  variant: BadgeVariant;
  className?: string;
};

interface StatusBadgeProps {
  status?: string;
  label?: string;
  variant?: BadgeVariant;
  className?: string;
  customConfig?: StatusConfigEntry;
}

const statusConfig: Record<string, StatusConfigEntry> = {
  // Application statuses
  approved: { label: 'Approved', variant: 'success' },
  pending: { label: 'Pending', variant: 'warning' },
  rejected: { label: 'Rejected', variant: 'destructive' },
  screening: { label: 'Screening', variant: 'info' },

  // Lead statuses
  active: { label: 'Active', variant: 'success' },
  inactive: { label: 'Inactive', variant: 'secondary' },

  // Showing statuses
  scheduled: { label: 'Scheduled', variant: 'info' },
  completed: { label: 'Completed', variant: 'secondary' },
  cancelled: { label: 'Cancelled', variant: 'destructive' },

  // Payment statuses
  succeeded: { label: 'Succeeded', variant: 'success' },
  failed: { label: 'Failed', variant: 'destructive' },
  refunded: { label: 'Refunded', variant: 'outline' },

  // Risk bands
  low: { label: 'Low Risk', variant: 'success' },
  medium: { label: 'Medium Risk', variant: 'warning' },
  high: { label: 'High Risk', variant: 'destructive' },
};

export const StatusBadge = memo(function StatusBadge({
  status,
  label,
  variant,
  className,
  customConfig
}: StatusBadgeProps) {
  // Use custom config if provided, otherwise use status config
  const config = customConfig || (status ? statusConfig[status.toLowerCase()] : undefined);
  
  if (!config && !label) {
    return null;
  }

  const displayLabel = label || config?.label || status;
  const badgeVariant = variant || config?.variant || 'secondary';
  const badgeClassName = className || config?.className;

  return (
    <Badge 
      variant={badgeVariant}
      className={cn(badgeClassName)}
    >
      {displayLabel}
    </Badge>
  );
});
